import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet,
  Download,
  Loader2,
  PieChart,
  Calendar,
  AlertTriangle,
  RefreshCw,
  Upload,
  CheckCircle,
  AlertCircle,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { API_URL } from "@/utils/api";

export default function BatchAnalysisTab() {
  const { formatAmount, currency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingMonth, setUploadingMonth] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchBatchAnalysis();
  }, []);

  // Extract available years from data
  useEffect(() => {
    if (analysisData?.monthly_breakdown) {
      const years = new Set();
      Object.keys(analysisData.monthly_breakdown).forEach(month => {
        const year = month.split("-")[0];
        if (year) years.add(year);
      });
      setAvailableYears(Array.from(years).sort().reverse());
    }
  }, [analysisData]);

  const fetchBatchAnalysis = async () => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/bank-statements/batch-analysis`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setAnalysisData(data);
      }
    } catch (err) {
      console.error("Error fetching batch analysis:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    const token = localStorage.getItem("auth_token");
    
    try {
      const res = await fetch(`${API_URL}/api/bank-statements/batch-analysis/export?format=${format}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bank_statement_analysis.${format === "excel" ? "xlsx" : format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Report exported as ${format.toUpperCase()}`);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  const handleUploadForMonth = (monthData) => {
    setUploadingMonth(monthData);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }
    
    setIsUploading(true);
    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("statement_name", `${uploadingMonth?.month_name || 'Statement'}`);
    
    try {
      const res = await fetch(`${API_URL}/api/bank-statements/upload`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Uploaded! Analyzed ${data.transaction_count} transactions.`);
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadingMonth(null);
        fetchBatchAnalysis();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  // Filter data by selected year
  const getFilteredData = () => {
    if (!analysisData || selectedYear === "all") return analysisData;
    
    const filteredMonthly = {};
    Object.entries(analysisData.monthly_breakdown || {}).forEach(([month, data]) => {
      if (month.startsWith(selectedYear)) {
        filteredMonthly[month] = data;
      }
    });
    
    // Recalculate summary for filtered data
    let totalIncome = 0, totalExpenses = 0, transactions = 0;
    Object.values(filteredMonthly).forEach(m => {
      totalIncome += m.income || 0;
      totalExpenses += m.expenses || 0;
      transactions += m.transactions || 0;
    });
    
    return {
      ...analysisData,
      monthly_breakdown: filteredMonthly,
      summary: {
        ...analysisData.summary,
        total_income: totalIncome,
        total_expenses: totalExpenses,
        total_transactions: transactions,
        net_amount: totalIncome - totalExpenses
      }
    };
  };

  // Get top income sources and expense categories
  const getTopBreakdowns = () => {
    if (!analysisData?.category_breakdown) return { topIncome: [], topExpense: [] };
    
    const categories = Object.entries(analysisData.category_breakdown).map(([name, data]) => ({
      name,
      income: data.income || 0,
      expenses: data.expenses || 0,
      count: data.count || 0
    }));
    
    const topIncome = categories
      .filter(c => c.income > 0)
      .sort((a, b) => b.income - a.income)
      .slice(0, 5);
    
    const topExpense = categories
      .filter(c => c.expenses > 0)
      .sort((a, b) => b.expenses - a.expenses)
      .slice(0, 5);
    
    return { topIncome, topExpense };
  };

  // Calculate YoY growth
  const calculateGrowth = () => {
    if (availableYears.length < 2) return null;
    
    const currentYear = availableYears[0];
    const previousYear = availableYears[1];
    
    let currentIncome = 0, currentExpense = 0;
    let previousIncome = 0, previousExpense = 0;
    
    Object.entries(analysisData?.monthly_breakdown || {}).forEach(([month, data]) => {
      if (month.startsWith(currentYear)) {
        currentIncome += data.income || 0;
        currentExpense += data.expenses || 0;
      } else if (month.startsWith(previousYear)) {
        previousIncome += data.income || 0;
        previousExpense += data.expenses || 0;
      }
    });
    
    const incomeGrowth = previousIncome > 0 ? ((currentIncome - previousIncome) / previousIncome) * 100 : 0;
    const expenseGrowth = previousExpense > 0 ? ((currentExpense - previousExpense) / previousExpense) * 100 : 0;
    
    return {
      currentYear,
      previousYear,
      incomeGrowth: Math.round(incomeGrowth),
      expenseGrowth: Math.round(expenseGrowth),
      currentIncome,
      previousIncome,
      currentExpense,
      previousExpense
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#121212]/40" />
      </div>
    );
  }

  if (!analysisData || analysisData.summary.total_statements === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-2 border-dashed border-[#121212]/20">
          <CardContent className="py-16">
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-[#121212]/40" />
              </div>
              <h3 className="text-lg font-semibold text-[#121212] mb-2">No Data for Analysis</h3>
              <p className="text-[#121212]/60">
                Upload bank statements first to see consolidated analysis and reports.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const { summary, category_breakdown, statements, missing_months, coverage } = filteredData;
  const monthly_breakdown = filteredData.monthly_breakdown;
  const { topIncome, topExpense } = getTopBreakdowns();
  const growth = calculateGrowth();

  // Get top categories by total amount
  const topCategories = Object.entries(category_breakdown || {})
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => Math.abs(b.income + b.expenses) - Math.abs(a.income + a.expenses))
    .slice(0, 8);

  // Get monthly data as array
  const monthlyData = Object.entries(monthly_breakdown || {})
    .map(([month, data]) => ({ month, ...data }))
    .slice(-12); // Last 12 months

  return (
    <div className="space-y-6">
      {/* Header with Currency Switcher, Year Filter, and Export Options */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#121212]">Consolidated Analysis</h2>
          <p className="text-sm text-[#121212]/60">
            {selectedYear === "all" ? "All time" : selectedYear} • {summary.total_statements} statements • {summary.total_transactions} transactions
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CurrencySwitcher />
          
          {/* Year Filter */}
          {availableYears.length > 0 && (
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px] rounded-full h-8">
                <Calendar className="w-4 h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchBatchAnalysis}
            className="rounded-full h-8"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Select onValueChange={handleExport} disabled={!!exporting}>
            <SelectTrigger className="w-[140px] rounded-full h-8">
              <SelectValue placeholder={exporting ? "..." : "Export"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">PDF Report</SelectItem>
              <SelectItem value="excel">Excel</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Top Income & Expense Highlights */}
      {(topIncome.length > 0 || topExpense.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Income Sources */}
          <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-green-700">
                <Sparkles className="w-5 h-5" />
                Top Income Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topIncome.slice(0, 4).map((cat, idx) => (
                  <div key={cat.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-sm">{cat.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-green-600">{formatAmount(cat.income)}</span>
                      <span className="text-xs text-gray-500 ml-2">({cat.count})</span>
                    </div>
                  </div>
                ))}
                {topIncome.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No income data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Expense Categories */}
          <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-red-700">
                <TrendingDown className="w-5 h-5" />
                Top Expense Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topExpense.slice(0, 4).map((cat, idx) => (
                  <div key={cat.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-sm">{cat.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-red-600">{formatAmount(cat.expenses)}</span>
                      <span className="text-xs text-gray-500 ml-2">({cat.count})</span>
                    </div>
                  </div>
                ))}
                {topExpense.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No expense data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Year-over-Year Growth (if multiple years) */}
      {growth && selectedYear === "all" && (
        <Card className="border border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-purple-800">Year-over-Year Growth ({growth.previousYear} → {growth.currentYear})</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Income Growth</p>
                <p className={`text-xl font-bold ${growth.incomeGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {growth.incomeGrowth >= 0 ? '+' : ''}{growth.incomeGrowth}%
                  {growth.incomeGrowth >= 0 ? <ArrowUpRight className="inline w-4 h-4" /> : <ArrowDownRight className="inline w-4 h-4" />}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Expense Growth</p>
                <p className={`text-xl font-bold ${growth.expenseGrowth <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {growth.expenseGrowth >= 0 ? '+' : ''}{growth.expenseGrowth}%
                  {growth.expenseGrowth >= 0 ? <ArrowUpRight className="inline w-4 h-4" /> : <ArrowDownRight className="inline w-4 h-4" />}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{growth.currentYear} Income</p>
                <p className="text-lg font-semibold">{formatAmount(growth.currentIncome)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{growth.previousYear} Income</p>
                <p className="text-lg font-semibold text-gray-600">{formatAmount(growth.previousIncome)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border border-[#BFFF00]/30 bg-gradient-to-br from-[#F7FFE5] to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <TrendingUp className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-[#121212]">{formatAmount(summary.total_income)}</p>
            <p className="text-xs text-[#121212]/60">Total Income</p>
          </CardContent>
        </Card>

        <Card className="border border-red-100 bg-gradient-to-br from-red-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-red-600" />
              <TrendingDown className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-[#121212]">{formatAmount(summary.total_expenses)}</p>
            <p className="text-xs text-[#121212]/60">Total Expenses</p>
          </CardContent>
        </Card>

        <Card className="border border-blue-100 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <p className={`text-2xl font-bold ${summary.net_amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatAmount(summary.net_amount)}
            </p>
            <p className="text-xs text-[#121212]/60">Net Amount</p>
          </CardContent>
        </Card>

        <Card className="border border-purple-100 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-[#121212]">{summary.total_transactions}</p>
            <p className="text-xs text-[#121212]/60">Transactions</p>
          </CardContent>
        </Card>

        <Card className={`border ${summary.suspense_items > 0 ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-white' : 'border-gray-100'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className={`w-5 h-5 ${summary.suspense_items > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
            </div>
            <p className="text-2xl font-bold text-[#121212]">{summary.suspense_items}</p>
            <p className="text-xs text-[#121212]/60">Needs Review</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <Card className="border border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Category Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topCategories.length > 0 ? (
            <div className="space-y-3">
              {topCategories.map((cat) => {
                const total = Math.abs(cat.income) + Math.abs(cat.expenses);
                const maxTotal = Math.max(...topCategories.map(c => Math.abs(c.income) + Math.abs(c.expenses)));
                const percentage = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                
                return (
                  <div key={cat.name} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-[#121212]">{cat.name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-green-600">+{formatAmount(cat.income)}</span>
                        <span className="text-red-600">-{formatAmount(cat.expenses)}</span>
                        <Badge variant="outline" className="text-xs">{cat.count}</Badge>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#BFFF00] to-[#9EE600] rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#121212]/60 text-center py-8">No category data available</p>
          )}
        </CardContent>
      </Card>

      {/* Monthly Breakdown */}
      <Card className="border border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Monthly Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Month</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Transactions</th>
                    <th className="text-right py-2 px-3 font-medium text-green-600">Income</th>
                    <th className="text-right py-2 px-3 font-medium text-red-600">Expenses</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((month) => {
                    const net = month.income - month.expenses;
                    return (
                      <tr key={month.month} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{month.month}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{month.transactions}</td>
                        <td className="py-2 px-3 text-right text-green-600">{formatAmount(month.income)}</td>
                        <td className="py-2 px-3 text-right text-red-600">{formatAmount(month.expenses)}</td>
                        <td className={`py-2 px-3 text-right font-medium ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatAmount(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[#121212]/60 text-center py-8">No monthly data available</p>
          )}
        </CardContent>
      </Card>

      {/* Coverage & Missing Months */}
      {coverage && (coverage.missing_months?.length > 0 || coverage.covered_months?.length > 0) && (
        <Card className={`border ${missing_months?.length > 0 ? 'border-amber-200 bg-amber-50/50' : 'border-green-200 bg-green-50/50'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {missing_months?.length > 0 ? (
                <AlertCircle className="w-5 h-5 text-amber-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              Data Coverage
              {coverage?.complete && (
                <Badge className="bg-green-100 text-green-700 ml-2">Complete</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Coverage Summary */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Period:</span>
                <span className="font-medium">
                  {coverage.start_month && coverage.end_month ? 
                    `${coverage.start_month} to ${coverage.end_month}` : 
                    'N/A'}
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600">Covered:</span>
                <span className="font-medium">{coverage.covered_months?.length || 0} months</span>
                {missing_months?.length > 0 && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-amber-600 font-medium">{missing_months.length} missing</span>
                  </>
                )}
              </div>
              
              {/* Missing Months List */}
              {missing_months?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-800">Missing months - upload to complete analysis:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {missing_months.map((month) => (
                      <div 
                        key={month.month_key}
                        className="flex items-center justify-between p-2 bg-white border border-amber-200 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium">{month.month_name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUploadForMonth(month)}
                          className="h-6 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                        >
                          <Upload className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Covered Months Visual */}
              {coverage.covered_months?.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-2">Covered months:</p>
                  <div className="flex flex-wrap gap-1">
                    {coverage.covered_months.map((month) => (
                      <Badge key={month} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        {month}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statements List */}
      <Card className="border border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Analyzed Statements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statements.length > 0 ? (
            <div className="space-y-2">
              {statements.map((stmt) => (
                <div key={stmt.statement_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-[#121212]">{stmt.name || "Untitled Statement"}</p>
                    <p className="text-xs text-[#121212]/60">
                      {stmt.transaction_count} transactions • {new Date(stmt.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-600">+{formatAmount(stmt.total_credits)}</p>
                    <p className="text-sm text-red-600">-{formatAmount(stmt.total_debits)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#121212]/60 text-center py-8">No statements found</p>
          )}
        </CardContent>
      </Card>

      {/* Upload Missing Month Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Upload Statement for {uploadingMonth?.month_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Upload the bank statement for <strong>{uploadingMonth?.month_name}</strong> to complete your analysis.
            </p>
            <div className="space-y-2">
              <Label>Statement File (PDF, Excel, CSV)</Label>
              <Input
                type="file"
                accept=".pdf,.xlsx,.xls,.csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="cursor-pointer"
              />
            </div>
            {uploadFile && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                {uploadFile.name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button 
              onClick={handleFileUpload}
              disabled={!uploadFile || isUploading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isUploading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload & Analyze</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
